import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isSecondaryOnly } from '../utils/dataBasis';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import API from '../services/api';
import { formatIsoDate } from '../utils/formatDateValue';
import { JumpOrigin } from '../utils/fieldAnchors';
import { validationApi } from '../services/validationApi';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import {
  SparklesIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SuggestionType = 'conflict' | 'fill' | 'enrich' | 'lifecycle' | 'escalation' | 'source';
type Decision = 'APPROVED' | 'REJECTED';
type Origin = 'dedup' | 'validation';

interface RawSuggestion {
  id: string;
  field: string;
  currentValue: unknown;
  suggestedValue: unknown;
  type: SuggestionType;
  priority?: number;
  groupId?: string | null;
  reason?: string;
  sourceUrl?: string;
  snippet?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
}

interface SourceCase {
  id: string;
  caseNumber: string;
  description?: string;
  sourceInformation?: string | null;
  dataSource?: string | null;
  createdAt?: string;
}

// A flattened, addressable suggestion. `container` is the match id (dedup) or
// validation-result id (validation) — the thing an Apply is grouped/dispatched by.
export interface Item extends RawSuggestion {
  origin: Origin;
  container: string;
  matchConfidence?: number;   // dedup only
  source: SourceCase;
}

const typeConfig: Record<SuggestionType, { label: string; color: string }> = {
  conflict: { label: 'Conflict', color: 'bg-red-100 text-red-700' },
  fill: { label: 'Fill', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  enrich: { label: 'Add', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  lifecycle: { label: 'Status', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  escalation: { label: 'Escalation', color: 'bg-red-100 text-red-800' },
  source: { label: 'Source', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
};

const isEmptyVal = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

const fmt = (v: unknown): string => {
  if (isEmptyVal(v)) return '(empty)';
  const asDate = formatIsoDate(v);
  if (asDate) return asDate;
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// Which detail-page section a field belongs to (for section-level badges).
type Section = 'victims' | 'perpetrators' | 'violations';
// Bare ViolationDetail field names (validation emits these directly, not as "violationDetails[..].x").
const VIOLATION_DETAIL_FIELDS = new Set([
  'perpetratorKnown', 'perpetratorTypes', 'perpetratorDetails', 'cause',
  'whyVictimsKilled', 'howKillingCarriedOut', 'whyVictimsCriminalized',
  'accusations', 'policiesOrLawsUsed', 'accusationsLegallyFiled', 'courtFilingLocation', 'courtFilingDetails',
  'victimsInPrison', 'prisonConditions', 'victimsSituationIfNotInPrison',
  'harassmentPrior', 'harassmentPriorDetails', 'legalCaseFiled', 'legalCaseDetails', 'legalCaseWhyNot',
  'stateActionUndertaken', 'stateActionDetails', 'stateActionWhyNot',
  'decisionOnAccusations', 'decisionDetails', 'whoFiledAccusations', 'whoFiledAccusationsDetails',
  'criminalizationTypes', 'criminalizationTriggers', 'legalInstrumentTypes', 'proceedingType',
  'isRetaliatoryProceeding', 'perpetratorAccountabilityStatus',
]);
const sectionOf = (field: string): Section | null => {
  if (/^victims(\[|$)/.test(field)) return 'victims';
  if (/^perpetrators(\[|$)/.test(field)) return 'perpetrators';
  if (/^violationDetails(\[|$)/.test(field) || field === 'violationTypes' || field === 'otherViolationTypes' || VIOLATION_DETAIL_FIELDS.has(field.replace(/^violationDetails\[[^\]]+\]\./, ''))) return 'violations';
  return null;
};
const isEntityField = (field: string) => ['victims', 'perpetrators', 'violationDetails'].includes(field);

// Fields that are two halves of a single logical change and should render in one card (not as
// separate, duplicate-looking suggestions). violationTypes + otherViolationTypes together record an
// "OTHER" violation and its label.
const COUPLED_FIELD_KEY: Record<string, string> = {
  violationTypes: 'violation-type-pair',
  otherViolationTypes: 'violation-type-pair',
};

// Turn a raw field path into a human label, e.g.
//   "violationDetails[abc].perpetratorTypes" → "Violation detail · Perpetrator types"
//   "communityImpact" → "Community impact"   ·   "victims" → "New victim"
const CONTEXT_LABEL: Record<string, string> = { victims: 'Victim', perpetrators: 'Perpetrator', violationDetails: 'Violation detail' };
const ADD_LABEL: Record<string, string> = { victims: 'New victim', perpetrators: 'New perpetrator', violationDetails: 'New violation detail' };
const humanizeLeaf = (leaf: string) =>
  leaf
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase())
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
// Compact, readable snippet of a URL for link text (drops protocol + www, truncates).
const urlSnippet = (url: string, max = 46): string => {
  const s = url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
  return s.length > max ? `${s.slice(0, max)}…` : s;
};
const prettyField = (field: string): string => {
  if (ADD_LABEL[field]) return ADD_LABEL[field];
  const m = field.match(/^(victims|perpetrators|violationDetails)\[[^\]]+\]\.(.+)$/);
  if (m) return `${CONTEXT_LABEL[m[1]]} · ${humanizeLeaf(m[2])}`;
  return humanizeLeaf(field);
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface Ctx {
  canReview: boolean;
  loading: boolean;
  items: Item[];              // all PENDING (incl. source)
  substantiveCount: number;   // non-source pending — drives visibility/badges
  decisions: Record<string, Decision>;
  busyContainer: string | null;
  setDecision: (item: Item, decision: Decision) => void;
  applyItems: (items: Item[]) => Promise<void>;
  // overrides: reviewer-edited values by suggestion id, applied instead of the proposed ones (dedup only).
  decideAndApply: (items: Item[], decision: Decision, overrides?: Record<string, unknown>) => Promise<void>;
  forFields: (fields: string[]) => Item[];
  forSection: (section: Section) => Item[];
  refresh: () => Promise<void>;
  jumpToField?: (path: string, origin?: JumpOrigin) => void;
  // Incremented by the page to ask the suggestions drawer to open (e.g. "Back to suggestions").
  openRequests: number;
}

const SuggestionsContext = createContext<Ctx | null>(null);
export const useSuggestions = () => {
  const c = useContext(SuggestionsContext);
  if (!c) throw new Error('useSuggestions must be used within <SuggestionsProvider>');
  return c;
};

export function SuggestionsProvider({
  caseId, caseData, onApplied, onJumpToField, openRequests = 0, children,
}: {
  caseId: string;
  caseData?: Record<string, any>;
  onApplied?: () => void;
  // Scroll the page to the section showing a field path (used by the suggestions drawer).
  onJumpToField?: (path: string, origin?: JumpOrigin) => void;
  openRequests?: number;
  children: React.ReactNode;
}) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canReview = user?.role === 'ADMIN' || user?.role === 'APPROVER';

  // Once a case is APPROVED it is final — no proposed updates are shown anymore.
  const caseApproved = caseData?.status === 'APPROVED';
  // Validation findings apply to bot-produced (auto-sourced) secondary-data cases, actioned by APPROVER/ADMIN.
  const validationEligible =
    !caseApproved && isSecondaryOnly(caseData) && caseData?.isAutoSourced === true && (user?.role === 'ADMIN' || user?.role === 'APPROVER');

  const [dedupUpdates, setDedupUpdates] = useState<RawSuggestion[]>([]);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [busyContainer, setBusyContainer] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Validation findings have no server-side "reject"; a rejected one is hidden locally for the session.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    // Proposed updates from related sources live in Case.metadata.ai.pendingUpdates (single source
    // of truth); this endpoint returns the PENDING ones. They still surface on an approved case — a
    // late-arriving duplicate can genuinely add info.
    if (!canReview) { setDedupUpdates([]); setLoading(false); return; }
    try {
      const res = await API.get(`/api/dedup/cases/${caseId}/updates`);
      setDedupUpdates(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDedupUpdates([]);
    } finally {
      setLoading(false);
    }
  }, [caseId, canReview]);

  useEffect(() => { refresh(); }, [refresh]);

  // Validation findings (shares the react-query cache with AiValidationPanel).
  const { data: validationStatus } = useQuery<any>({
    queryKey: ['validation-status', caseId],
    queryFn: () => validationApi.getStatus(caseId),
    enabled: !!validationEligible,
  });

  const dedupItems: Item[] = useMemo(() => {
    // Flat pendingUpdates from metadata — all belong to THIS case, so they share one container (caseId).
    return (dedupUpdates || [])
      .filter((s) => s && s.status === 'PENDING')
      .map((s: any) => {
        const url: string | undefined = s.source || s.sourceUrl || undefined;
        return {
          ...s,
          sourceUrl: url,
          origin: 'dedup' as Origin,
          container: caseId,
          source: { id: '', caseNumber: 'Related source', dataSource: 'Related source', sourceInformation: url ?? null } as SourceCase,
        };
      });
  }, [dedupUpdates, caseId]);

  // When auto-apply is active, validation findings are handled by the AI Validation panel
  // (auto-applied inline, conflicts, notes) — so the legacy validation "proposed updates"
  // badges/bar are redundant and suppressed here. Dedup suggestions are unaffected.
  const autoApplyActive =
    ((caseData?.metadata?.ai?.autoApplied?.length ?? 0) > 0) ||
    ((caseData?.metadata?.ai?.conflicts?.length ?? 0) > 0);

  const validationItems: Item[] = useMemo(() => {
    if (!validationEligible || autoApplyActive) return [];
    const latest = validationStatus?.latest;
    if (!latest?.aiResponse?.findings) return [];
    const src: SourceCase = { id: latest.id, caseNumber: 'AI validation', dataSource: 'AI validation' };
    const out: Item[] = [];
    latest.aiResponse.findings.forEach((f: any, fi: number) => {
      (f.fixes || []).forEach((fix: any, xi: number) => {
        if (fix.applied) return;
        const id = `val::${latest.id}::${fi}::${xi}`;
        if (dismissed.has(id)) return;
        let type: SuggestionType;
        let currentValue: unknown = null;
        if (isEntityField(fix.field)) {
          type = 'enrich';
        } else {
          const cur = caseData ? caseData[fix.field] : undefined;
          if (!isEmptyVal(cur)) { type = 'conflict'; currentValue = cur; }
          else { type = 'fill'; }
        }
        out.push({
          id, field: fix.field, currentValue, suggestedValue: fix.suggestedValue,
          type, priority: type === 'conflict' ? 0 : 2, groupId: null,
          reason: f.message, sourceUrl: f.source, snippet: '', status: 'PENDING',
          origin: 'validation', container: latest.id, source: { ...src, sourceInformation: f.source ?? null },
        });
      });
    });
    return out;
  }, [validationEligible, autoApplyActive, validationStatus, dismissed, caseData]);

  const items = useMemo(() => [...dedupItems, ...validationItems], [dedupItems, validationItems]);
  const substantiveCount = useMemo(() => items.filter((i) => i.type !== 'source').length, [items]);

  // Deciding one item decides coupled items (same container + same groupId) together.
  const setDecision = useCallback((item: Item, decision: Decision) => {
    setDecisions((prev) => {
      const next = { ...prev };
      const siblings = item.groupId
        ? items.filter((i) => i.container === item.container && i.groupId === item.groupId)
        : [item];
      for (const s of siblings) next[s.id] = decision;
      return next;
    });
  }, [items]);

  const applyItems = useCallback(async (subset: Item[]) => {
    const byContainer = new Map<string, Item[]>();
    for (const it of subset) byContainer.set(it.container, [...(byContainer.get(it.container) || []), it]);

    let applied = 0;
    let touched = false;
    let didValidation = false;
    const newlyDismissed: string[] = [];

    for (const [container, group] of Array.from(byContainer.entries())) {
      const decided = group.filter((it) => decisions[it.id]);
      if (decided.length === 0) continue;
      touched = true;
      setBusyContainer(container);
      try {
        if (group[0].origin === 'dedup') {
          // container is the caseId; proposed updates live in the case's metadata.
          const payload = decided.map((it) => ({ id: it.id, decision: decisions[it.id] }));
          const res = await API.post(`/api/dedup/cases/${container}/updates/apply`, { decisions: payload });
          applied += res.data?.appliedCount ?? 0;
        } else {
          // validation: apply approved field(s); rejected ones are dismissed locally.
          const approvedFields = Array.from(new Set(decided.filter((it) => decisions[it.id] === 'APPROVED').map((it) => it.field)));
          if (approvedFields.length > 0) {
            const res = await validationApi.applySuggestions(container, approvedFields);
            applied += res.applied?.length ?? approvedFields.length;
          }
          decided.filter((it) => decisions[it.id] === 'REJECTED').forEach((it) => newlyDismissed.push(it.id));
          didValidation = true;
        }
      } catch {
        showToast('error', 'Failed to apply some updates');
      } finally {
        setBusyContainer(null);
      }
    }

    if (!touched) { showToast('error', 'Approve or reject an update first'); return; }
    if (newlyDismissed.length) setDismissed((prev) => new Set([...Array.from(prev), ...newlyDismissed]));
    showToast('success', `Applied ${applied} update${applied === 1 ? '' : 's'}`);
    await refresh();
    if (didValidation) queryClient.invalidateQueries({ queryKey: ['validation-status', caseId] });
    onApplied?.();
  }, [decisions, refresh, onApplied, showToast, queryClient, caseId]);

  // Direct per-suggestion action — apply or dismiss a coupled group immediately (no mark-then-batch).
  const decideAndApply = useCallback(async (subset: Item[], decision: Decision, overrides: Record<string, unknown> = {}) => {
    if (subset.length === 0) return;
    const container = subset[0].container;
    const origin = subset[0].origin;
    setBusyContainer(container);
    const newlyDismissed: string[] = [];
    let applied = 0;
    try {
      if (origin === 'dedup') {
        const res = await API.post(`/api/dedup/cases/${container}/updates/apply`, {
          decisions: subset.map((it) => ({ id: it.id, decision, ...(decision === 'APPROVED' && it.id in overrides ? { value: overrides[it.id] } : {}) })),
        });
        applied = res.data?.appliedCount ?? 0;
      } else if (decision === 'APPROVED') {
        const fields = Array.from(new Set(subset.map((it) => it.field)));
        const res = await validationApi.applySuggestions(container, fields);
        applied = res.applied?.length ?? fields.length;
      } else {
        subset.forEach((it) => newlyDismissed.push(it.id));
      }
      const edited = Object.keys(overrides).length;
      showToast('success', decision === 'APPROVED' ? `Applied ${applied || subset.length} update${(applied || subset.length) === 1 ? '' : 's'}${edited ? ` (${edited} with your edited value)` : ''}` : 'Dismissed');
    } catch {
      showToast('error', 'Failed to apply update');
    } finally {
      setBusyContainer(null);
    }
    if (newlyDismissed.length) setDismissed((prev) => new Set([...Array.from(prev), ...newlyDismissed]));
    await refresh();
    if (origin === 'validation') queryClient.invalidateQueries({ queryKey: ['validation-status', caseId] });
    onApplied?.();
  }, [refresh, onApplied, showToast, queryClient, caseId]);

  const forFields = useCallback((fields: string[]) => items.filter((i) => fields.includes(i.field)), [items]);
  const forSection = useCallback((section: Section) => items.filter((i) => sectionOf(i.field) === section), [items]);

  const value: Ctx = {
    canReview, loading, items, substantiveCount, decisions, busyContainer,
    setDecision, applyItems, decideAndApply, forFields, forSection, refresh,
    jumpToField: onJumpToField,
    openRequests,
  };

  return <SuggestionsContext.Provider value={value}>{children}</SuggestionsContext.Provider>;
}

// ---------------------------------------------------------------------------
// Shared rendering — a list of items grouped by source, with controls
// ---------------------------------------------------------------------------

// Human-readable summary of a new entity (instead of raw JSON).
function describeEntity(field: string, value: unknown): { noun: string; title: string; subtitle: string } {
  const o = (value && typeof value === 'object' ? value : {}) as Record<string, any>;
  const join = (arr: (string | null | undefined)[]) => arr.filter(Boolean).join(' · ');
  const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : v ? [String(v)] : []);
  if (field.startsWith('victims')) {
    if (o.isGroup) {
      const counts = [
        o.affectedIndividualsCount != null ? `${o.affectedIndividualsCount} individuals` : null,
        o.affectedFamiliesCount != null ? `${o.affectedFamiliesCount} families` : null,
        o.affectedCommunitiesCount != null ? `${o.affectedCommunitiesCount} communities` : null,
      ];
      return { noun: 'victim group', title: asList(o.groupName).join(', ') || 'Group', subtitle: join(counts) };
    }
    const name = `${o.firstName || ''} ${o.lastName || ''}`.trim() || 'Unnamed victim';
    return { noun: 'victim', title: name, subtitle: join([o.communityRole, o.indigenousGroupOfVictim, o.gender && o.gender !== 'UNKNOWN' ? o.gender : null]) };
  }
  if (field.startsWith('perpetrators')) {
    const name = o.name || `${o.firstName || ''} ${o.lastName || ''}`.trim() || 'Perpetrator';
    return { noun: 'perpetrator', title: name, subtitle: join([o.type, o.position, o.affiliation]) };
  }
  if (field.startsWith('violationDetails')) {
    const detail = (o.perpetratorDetails || o.howKillingCarriedOut || o.cause || '') as string;
    return { noun: 'violation', title: o.violationType || 'Violation detail', subtitle: detail.slice(0, 140) };
  }
  return { noun: 'item', title: fmt(value), subtitle: '' };
}

// Editing a proposed value: strings edit in a textarea, dates in a date input, numbers in a
// number input, lists as comma-separated text. New-entity suggestions can't be edited here.
const editKind = (v: unknown): 'text' | 'date' | 'number' | 'list' | 'bool' | null => {
  if (Array.isArray(v)) return v.every((x) => typeof x !== 'object') ? 'list' : null;
  if (typeof v === 'number') return 'number';
  if (typeof v === 'boolean') return 'bool';
  if (typeof v === 'string') return formatIsoDate(v) && /^\d{4}-\d{2}-\d{2}/.test(v) ? 'date' : 'text';
  return null;
};
const toEditText = (v: unknown): string => Array.isArray(v) ? v.join(', ') : typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) ? v.slice(0, 10) : String(v ?? '');
const fromEditText = (kind: ReturnType<typeof editKind>, text: string): unknown => {
  if (kind === 'list') return text.split(',').map((x) => x.trim()).filter(Boolean);
  if (kind === 'number') return Number(text);
  if (kind === 'bool') return text === 'true';
  return text;
};

function ItemRow({ item, onJump, editing, editText, onEditText, onToggleEdit }: {
  item: Item; onJump?: (path: string) => void;
  editing?: boolean; editText?: string; onEditText?: (t: string) => void; onToggleEdit?: () => void;
}) {
  const isEntity = isEntityField(item.field);
  const kind = isEntity ? null : editKind(item.suggestedValue);
  const cfg = typeConfig[item.type] || typeConfig.fill;
  const entityVals = isEntity ? (Array.isArray(item.suggestedValue) ? item.suggestedValue : [item.suggestedValue]) : null;
  const src = item.sourceUrl;
  return (
    <div className="mb-2 last:mb-0">
      {/* 1. description first (plain-language explanation) */}
      {item.reason && <div className="text-sm text-gray-700 dark:text-gray-300 mb-1.5">{item.reason}</div>}

      {/* 2. field label */}
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${cfg.color}`}>{cfg.label}</span>
        {onJump ? (
          <button type="button" onClick={() => onJump(item.field)} title="Show this field on the page" className="inline-flex items-center gap-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:underline">
            {prettyField(item.field)}
            <ArrowRightIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
          </button>
        ) : (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{prettyField(item.field)}</span>
        )}
        {item.groupId && <span className="text-xs text-gray-400">(linked)</span>}
      </div>

      {/* 3. the values side by side: what the case says now, what the source proposes, and —
             when the reviewer edits — what will actually be applied */}
      {entityVals ? (
        <div className="space-y-1">
          {entityVals.map((v, i) => {
            const e = describeEntity(item.field, v);
            return (
              <div key={i}>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  <span className="text-xs text-gray-500">Add {e.noun}:</span> <span className="font-medium">{e.title}</span>
                </div>
                {e.subtitle && <div className="text-xs text-gray-500 dark:text-gray-400">{e.subtitle}</div>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`grid gap-2 ${editing && kind ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
          <div className="rounded-md border border-gray-200 dark:border-gray-700 px-2.5 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Now on the case</div>
            <div className={`text-sm ${isEmptyVal(item.currentValue) ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>{isEmptyVal(item.currentValue) ? 'Nothing recorded' : fmt(item.currentValue)}</div>
          </div>
          <div className={`rounded-md border px-2.5 py-2 ${editing && kind ? 'border-gray-200 dark:border-gray-700' : 'border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-900/10'}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Proposed by the source</div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{fmt(item.suggestedValue)}</div>
            {kind && onToggleEdit && !editing && (
              <button type="button" onClick={onToggleEdit} className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:underline">Edit before applying</button>
            )}
          </div>
          {editing && kind && (
            <div className="rounded-md border border-emerald-400 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-900/10 px-2.5 py-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-1">Your value (will be applied)</div>
              {kind === 'text' ? (
                <textarea value={editText ?? ''} onChange={(e) => onEditText?.(e.target.value)} rows={3} autoFocus className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm text-sm" aria-label="Your value" />
              ) : kind === 'bool' ? (
                <select value={editText ?? 'false'} onChange={(e) => onEditText?.(e.target.value)} className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm text-sm" aria-label="Your value">
                  <option value="true">Yes</option><option value="false">No</option>
                </select>
              ) : (
                <input type={kind === 'date' ? 'date' : kind === 'number' ? 'number' : 'text'} value={editText ?? ''} onChange={(e) => onEditText?.(e.target.value)} autoFocus className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm text-sm" aria-label="Your value" />
              )}
              <div className="mt-1 flex flex-wrap items-center gap-x-3 text-[11px]">
                {kind === 'list' && <span className="text-gray-500 dark:text-gray-400">Separate items with commas.</span>}
                <button type="button" onClick={onToggleEdit} className="text-gray-500 hover:underline">Discard my edit</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4. snippet — source (per suggestion) */}
      {(item.snippet || src) && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {item.snippet && <span className="italic">“{item.snippet}”</span>}
          {src && (<>{item.snippet ? ' — ' : ''}<a href={src} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 underline break-all">{urlSnippet(src)}</a></>)}
        </div>
      )}

    </div>
  );
}

// Renders each suggestion as its own card (consistent with the AI Validation panel), with a direct
// per-suggestion Apply / Dismiss. Coupled suggestions (shared groupId) live in one card.
export function SuggestionList({ items, onJump }: { items: Item[]; onJump?: (path: string) => void }) {
  const { decideAndApply, busyContainer, canReview } = useSuggestions();

  const cards = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const it of [...items].sort((a, b) => (a.priority ?? 2) - (b.priority ?? 2))) {
      // Couple fields that are two halves of one logical change so they share a card instead of
      // looking like duplicates. An "OTHER" violation is recorded as OTHER in `violationTypes` PLUS
      // its label in `otherViolationTypes`; group them even when the source didn't set a groupId.
      const coupleKey = COUPLED_FIELD_KEY[it.field];
      const key = `${it.container}::${it.groupId || coupleKey || it.id}`;
      m.set(key, [...(m.get(key) || []), it]);
    }
    return Array.from(m.values());
  }, [items]);

  // Reviewer edits by suggestion id (text as typed), only for related-source suggestions, which
  // the server can apply with an override.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const toggleEdit = (it: Item) => setEdits((prev) => {
    const next = { ...prev };
    if (it.id in next) delete next[it.id]; else next[it.id] = toEditText(it.suggestedValue);
    return next;
  });
  const overridesFor = (coupled: Item[]): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const it of coupled) {
      if (!(it.id in edits)) continue;
      const kind = editKind(it.suggestedValue);
      const v = fromEditText(kind, edits[it.id]);
      if (JSON.stringify(v) !== JSON.stringify(it.suggestedValue)) out[it.id] = v;
    }
    return out;
  };

  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      {cards.map((coupled, gi) => {
        const container = coupled[0].container;
        const busy = busyContainer === container;
        const editable = coupled[0].origin === 'dedup';
        const overrides = editable ? overridesFor(coupled) : {};
        const edited = Object.keys(overrides).length > 0;
        return (
          <div key={gi} className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
            {coupled.map((it) => (
              <ItemRow
                key={it.id}
                item={it}
                onJump={onJump}
                editing={it.id in edits}
                editText={edits[it.id]}
                onEditText={(t) => setEdits((prev) => ({ ...prev, [it.id]: t }))}
                onToggleEdit={editable ? () => toggleEdit(it) : undefined}
              />
            ))}
            {canReview && (
              <div className="flex items-center gap-2 mt-2">
                {/* Direct per-suggestion action — applies/dismisses immediately (like AI Validation). */}
                <button
                  onClick={() => decideAndApply(coupled, 'REJECTED')}
                  disabled={busy}
                  className="px-3 py-1 text-xs font-medium rounded-md border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  {coupled.every((s) => isEmptyVal(s.currentValue) || isEntityField(s.field)) ? 'Dismiss' : 'Keep current'}
                </button>
                <button
                  onClick={() => decideAndApply(coupled, 'APPROVED', overrides)}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {busy ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CheckCircleIcon className="h-3.5 w-3.5" />}
                  {edited ? 'Apply my value' : 'Apply proposed'}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card — an always-visible panel of dedup (related-source) suggestions, shown
// above the case information. Replaces the compact badge/modal. Renders nothing
// when there are no substantive related-source suggestions.
// ---------------------------------------------------------------------------

export function SuggestionsCard() {
  const { items, jumpToField, openRequests } = useSuggestions();
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerShown, setDrawerShown] = useState(false); // drives the slide-in/out animation
  const openDrawer = () => { setModalOpen(true); requestAnimationFrame(() => setDrawerShown(true)); };
  const closeDrawer = () => { setDrawerShown(false); setTimeout(() => setModalOpen(false), 300); };
  const jumpFromDrawer = jumpToField ? (path: string) => { closeDrawer(); jumpToField(path, { kind: 'suggestions' }); } : undefined;
  // Reopen when the page asks (after a "Back to suggestions").
  useEffect(() => {
    if (openRequests > 0) openDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequests]);

  const dedupItems = items.filter((i) => i.origin === 'dedup');
  const proposedCount = dedupItems.filter((i) => i.type !== 'source').length;
  if (proposedCount === 0) return null;

  return (
    <>
      {/* Sidebar summary card — the red count opens a right slide-over (mirrors AI Validation) */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
            <SparklesIcon className="h-4 w-4 text-violet-500" /> Proposed Updates from Related Sources
          </h3>
          <button
            type="button"
            onClick={openDrawer}
            title="Review the proposed updates"
            aria-label={`Review ${proposedCount} proposed update${proposedCount === 1 ? '' : 's'}`}
            className="inline-flex shrink-0 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-red-600 text-white hover:bg-red-700 tabular-nums"
          >
            {proposedCount}
          </button>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className={`fixed inset-0 bg-gray-500/60 transition-opacity duration-300 ${drawerShown ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeDrawer}
          />
          <div
            className={`fixed inset-y-0 right-0 w-full max-w-3xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerShown ? 'translate-x-0' : 'translate-x-full'}`}
          >
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between shrink-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-violet-500" /> Proposed Updates from Related Sources
              </h3>
              <button onClick={closeDrawer} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">For each update: keep what the case says now, apply what the related source says, or apply your own wording. Nothing changes until you choose.</p>
              <SuggestionList items={dedupItems} onJump={jumpFromDrawer} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

